import * as React from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, ExternalLinkIcon } from 'lucide-react';
import {
  FaArchive,
  FaBullhorn,
  FaCalendar,
  FaCubes,
  FaMoneyBill,
  FaShip,
  FaStoreAlt,
  FaUserShield,
  FaUsers,
  FaMobile,
  FaBoxOpen,
} from 'react-icons/fa';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Routes } from '@/utils/routes';
import {
  CanAccessPartnerData,
  IsVytalEmployee,
  IsMarketing,
  Viewer,
  IsFinance,
  CanAccessUserData,
  IsAdmin,
  IsHumanResources,
  HasPartnerDataClaim,
  IsEventCleaningEmployeeClaim,
  CanAccessEventOverview,
  CanAccessOperationsData,
  CanAccessPickingJobs,
  IsEventManagerClaim,
} from '@/providers/ViewerContextProvider';
import i18n from '@/utils/i18n';
import { hasClickHouse, isLocalDev } from '@/utils/isDev';
import { useSidebarContext } from '../SidebarContext';
import { useSidebarLayout } from './useSidebarLayout';
import { cn } from '@/lib/utils';
import { ContextSidebar } from './ContextSidebar';
import type { IconName } from '@/utils/iconName';
import { FcEnteringHeavenAlive } from 'react-icons/fc';
import SvgIcon from '../Icon/SvgIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { SidebarUserMenu } from '../SidebarUserMenu';
import { useIsMobile } from '@/hooks/use-mobile';

interface NavItem {
  label: string;
  icon?:
    | React.ReactNode
    | React.ComponentType<{ className?: string; size?: string | number }>;
  iconName?: IconName;
  initiallyOpened?: boolean;
  links: {
    label: string;
    link: string;
    isExternal?: boolean;
  }[];
}

/**
 * Only the roles CanAccessPickingJobs lists get the entry: the backend refuses
 * everyone else, external event owners included.
 */
const pickingJobsLinks = (viewer: Viewer): NavItem['links'] =>
  CanAccessPickingJobs(viewer)
    ? [{ label: i18n.t('Picking:pageTitle'), link: Routes.pickingJobs() }]
    : [];

/**
 * Lives in the one Operations group. CanAccessOperationsData admits only roles
 * that CanAccessPartnerData admits too, and neither is the event-manager
 * layout, so every viewer who gets the link also gets that group.
 */
const opsWarningsLinks = (viewer: Viewer): NavItem['links'] =>
  CanAccessOperationsData(viewer) && hasClickHouse()
    ? [{ label: i18n.t('Navigation:opsWarnings'), link: Routes.opsWarnings() }]
    : [];

const getAvailableNavItems = (viewer: Viewer): NavItem[] => {
  const items: NavItem[] = [];
  const isDevLocal = isLocalDev();
  const hasCustomNavbarLayout = IsEventManagerClaim(viewer);

  if (CanAccessPartnerData(viewer) && !hasCustomNavbarLayout) {
    items.push({
      label: i18n.t('Navigation:partner'),
      icon: FaStoreAlt,
      initiallyOpened: true,
      links: [
        { label: i18n.t('Navigation:search'), link: Routes.partner() },
        {
          label: i18n.t('Navigation:storeMap'),
          link: Routes.storeMap(),
          isExternal: true,
        },
        { label: i18n.t('Navigation:stores'), link: Routes.stores() },
        { label: i18n.t('Navigation:storeAssets'), link: Routes.storeAssets() },
        {
          label: i18n.t('Navigation:inventoryChecks'),
          link: Routes.inventoryChecks(),
        },
      ],
    });

    items.push({
      label: i18n.t('Navigation:legacy'),
      icon: FaArchive,
      initiallyOpened: false,
      links: [
        {
          label: i18n.t('Navigation:legacyBrands'),
          link: Routes.legacyBrandSearch(),
        },
      ],
    });

    items.push({
      label: i18n.t('Navigation:operations'),
      icon: FaShip,
      initiallyOpened: true,
      links: [
        {
          label: i18n.t('Order:orders'),
          link: Routes.orders(),
        },
        { label: i18n.t('Navigation:storeAssets'), link: Routes.storeAssets() },
        {
          label: i18n.t('Navigation:miniReturners'),
          link: Routes.miniReturners(),
        },
        ...pickingJobsLinks(viewer),
        ...opsWarningsLinks(viewer),
      ],
    });

    items.push({
      label: i18n.t('Navigation:containers'),
      icon: FaCubes,
      links: [
        {
          label: i18n.t('Navigation:searchContainers'),
          link: Routes.containers(),
        },
        {
          label: i18n.t('Navigation:addContainerCollections'),
          link: Routes.addContainerCollections(),
        },
        {
          label: i18n.t('Navigation:searchContainerCollections'),
          link: Routes.containerCollectionByLocation(),
        },
        {
          label: i18n.t('Navigation:searchContainerStations'),
          link: Routes.containerStationSearch(),
        },
        {
          label: i18n.t('Navigation:containerTypeLabels'),
          link: Routes.containerTypes(),
        },
        {
          label: i18n.t('Navigation:qrCodeRedirectionCampaign'),
          link: Routes.containerQrRedirect(),
        },
      ],
    });

    items.push({
      label: i18n.t('Navigation:items'),
      icon: FaBoxOpen,
      links: [
        {
          label: i18n.t('Navigation:searchContainerStations'),
          link: Routes.containerStationSearch(),
        },
        {
          label: i18n.t('Navigation:searchItemLocations'),
          link: Routes.itemLocationSearch(),
        },
        {
          label: i18n.t('Navigation:searchAssets'),
          link: Routes.eventAssetSearch(),
        },
      ],
    });

    items.push({
      label: i18n.t('Navigation:events'),
      icon: FaCalendar,
      links: [
        {
          label: i18n.t('Navigation:events'),
          link: Routes.events(),
        },
        ...(CanAccessEventOverview(viewer)
          ? [
              {
                label: i18n.t('Navigation:eventOverview'),
                link: Routes.eventOverview(),
              },
            ]
          : []),
        {
          label: i18n.t('Navigation:eventAccounts'),
          link: Routes.eventAccounts(),
        },
        {
          label: i18n.t('Navigation:scanTunnel'),
          link: Routes.scanTunnels(),
        },
        {
          label: i18n.t('Navigation:searchHandoutsReturns'),
          link: Routes.orderFulfillmentSearch(),
        },
        {
          label: i18n.t('Navigation:createHandoutReturn'),
          link: Routes.createOrderFulfillment(),
        },
        {
          label: i18n.t('Navigation:unlinkedHandoutReturnPictures'),
          link: Routes.unlinkedOrderFulfillmentPictures(),
        },
      ],
    });
  }

  if (IsMarketing(viewer)) {
    items.push({
      label: i18n.t('Navigation:marketing'),
      icon: FaBullhorn,
      initiallyOpened: true,
      links: [
        { label: i18n.t('Navigation:campaigns'), link: Routes.campaigns() },
        { label: i18n.t('Navigation:news'), link: Routes.news() },
        { label: 'Sweepstakes', link: Routes.sweepstakesManagement() },
      ],
    });
  }

  if (IsFinance(viewer)) {
    items.push({
      label: i18n.t('Navigation:finance'),
      icon: FaMoneyBill,
      initiallyOpened: true,
      links: [
        { label: 'Service Packages', link: Routes.servicePackages() },
        { label: i18n.t('Navigation:bills'), link: Routes.billing() },
        { label: i18n.t('Navigation:reports'), link: Routes.finance() },
        {
          label: i18n.t('Navigation:customBillomatArticles'),
          link: Routes.editCustomBillomatArticles(),
        },
        {
          label: i18n.t('Navigation:invoiceCollections'),
          link: Routes.invoiceCollections(),
        },
        {
          label: i18n.t('Navigation:accountLedgerCsvUpload'),
          link: Routes.accountLedgerCsvUpload(),
        },
        {
          label: i18n.t('Navigation:financeBulkImport'),
          link: Routes.financeBulkImport(),
        },
        { label: 'Monthly invoicing', link: Routes.monthlyInvoicing() },
      ],
    });
  }

  const userLinks: NavItem['links'] = [];
  if (CanAccessUserData(viewer) || IsHumanResources(viewer)) {
    userLinks.push({
      label: i18n.t('Navigation:searchUsers'),
      link: Routes.users(),
    });
  }
  if (HasPartnerDataClaim(viewer)) {
    userLinks.push({
      label: i18n.t('Navigation:analyticsUsers'),
      link: Routes.analyticsUsers(),
    });
  }
  if (userLinks.length > 0) {
    items.push({
      label: i18n.t('Navigation:users'),
      icon: FaUsers,
      links: userLinks,
    });
  }

  if (IsAdmin(viewer) && isDevLocal) {
    items.push({
      label: i18n.t('Navigation:admin'),
      icon: FaUserShield,
      links: [
        { label: 'Edit Container Prices', link: Routes.editContainerPrices() },
        { label: 'Edit Container Info', link: Routes.editContainerInfo() },
        { label: 'Edit Item types', link: Routes.editItemTypes() },
        { label: 'Integration API Tools', link: Routes.integrationApiTools() },
      ],
    });
  }

  if (IsVytalEmployee(viewer) && !hasCustomNavbarLayout) {
    items.push({
      label: i18n.t('Navigation:app'),
      icon: FaMobile,
      links: [
        {
          label: i18n.t('Navigation:appTranslations'),
          link: Routes.appTranslations(),
        },
      ],
    });
  }

  if (IsEventManagerClaim(viewer)) {
    items.push({
      label: i18n.t('Navigation:events'),
      icon: FaCalendar,
      initiallyOpened: true,
      links: [
        { label: i18n.t('Navigation:events'), link: Routes.events() },
        {
          label: i18n.t('Navigation:eventAccounts'),
          link: Routes.eventAccounts(),
        },
        { label: i18n.t('Navigation:scanTunnel'), link: Routes.scanTunnels() },
        {
          label: i18n.t('Navigation:searchHandoutsReturns'),
          link: Routes.orderFulfillmentSearch(),
        },
        {
          label: i18n.t('Navigation:createHandoutReturn'),
          link: Routes.createOrderFulfillment(),
        },
        {
          label: i18n.t('Navigation:unlinkedHandoutReturnPictures'),
          link: Routes.unlinkedOrderFulfillmentPictures(),
        },
        // The Operations group, where everyone else finds picking jobs, is
        // hidden for this layout.
        ...pickingJobsLinks(viewer),
      ],
    });

    items.push({
      label: i18n.t('Navigation:items'),
      icon: FaBoxOpen,
      initiallyOpened: true,
      links: [
        {
          label: i18n.t('Navigation:searchContainerStations'),
          link: Routes.containerStationSearch(),
        },
        {
          label: i18n.t('Navigation:searchItemLocations'),
          link: Routes.itemLocationSearch(),
        },
      ],
    });
  }

  if (IsEventCleaningEmployeeClaim(viewer)) {
    items.push({
      label: i18n.t('Navigation:events'),
      icon: FaCalendar,
      initiallyOpened: true,
      links: [
        {
          label: i18n.t('Navigation:eventOverview'),
          link: Routes.eventOverview(),
        },
      ],
    });

    items.push({
      label: i18n.t('Navigation:items'),
      icon: FaBoxOpen,
      initiallyOpened: true,
      links: [
        {
          label: i18n.t('Navigation:searchContainerStations'),
          link: Routes.containerStationSearch(),
        },
        {
          label: i18n.t('Navigation:searchItemLocations'),
          link: Routes.itemLocationSearch(),
        },
        {
          label: i18n.t('Navigation:containerTypeLabels'),
          link: Routes.containerTypes(),
        },
      ],
    });
  }

  return items;
};

interface NavGroupProps {
  item: NavItem;
}

/**
 * `icon` is either a component or an already-rendered node. A `typeof`
 * function check is not enough to tell them apart: forwardRef/memo components
 * (e.g. every lucide-react icon) are exotic *objects* carrying $$typeof, and
 * rendering one as a child throws React #31.
 */
function NavItemIcon({ icon }: { icon: NavItem['icon'] }) {
  if (icon == null || typeof icon === 'boolean') return null;
  if (
    typeof icon === 'function' ||
    (typeof icon === 'object' &&
      '$$typeof' in icon &&
      !React.isValidElement(icon))
  ) {
    const IconComponent = icon as React.ComponentType<{
      className?: string;
      size?: string | number;
    }>;
    return <IconComponent className="tw:size-4" />;
  }
  return <>{icon}</>;
}

function NavGroup({ item }: NavGroupProps) {
  const location = useLocation();
  const { isMobile, setOpenMobile, open, setOpen } = useSidebar();

  const currentPath = location.pathname.split('?')[0].replace(/\//g, '');

  // Check if any sub-link is active
  const hasActiveChild = item.links.some(
    (linkInfo) => currentPath === linkInfo.link.replace(/\//g, ''),
  );

  const handleMobileClose = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Collapsible
      defaultOpen={item.initiallyOpened || hasActiveChild}
      className="tw:group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger
          asChild
          onClick={(e) => {
            if (!open) {
              e.preventDefault();
              setOpen(true);
            }
          }}
        >
          <SidebarMenuButton tooltip={item.label} className="tw:cursor-pointer">
            {item.iconName && (
              <SvgIcon name={item.iconName} className="tw:size-4" />
            )}
            <NavItemIcon icon={item.icon} />
            {!item.icon && !item.iconName && (
              <FcEnteringHeavenAlive className="tw:size-4" />
            )}
            <span>{item.label}</span>
            <ChevronRight className="tw:ml-auto tw:transition-transform tw:duration-200 tw:group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.links.map((linkInfo) => {
              const isActive = currentPath === linkInfo.link.replace(/\//g, '');
              const isTruncated = linkInfo.label.length > 24;
              return (
                <Tooltip
                  delayDuration={isTruncated ? 200 : 999999}
                  key={linkInfo.label}
                >
                  <TooltipTrigger asChild>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton asChild isActive={isActive}>
                        {linkInfo.isExternal ? (
                          <a
                            href={linkInfo.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tw:no-underline!"
                          >
                            <span>{linkInfo.label}</span>
                            <ExternalLinkIcon className="tw:size-2 tw:ml-auto" />
                          </a>
                        ) : (
                          <Link
                            to={linkInfo.link}
                            onClick={handleMobileClose}
                            className="tw:no-underline!"
                          >
                            <span>{linkInfo.label}</span>
                          </Link>
                        )}
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </TooltipTrigger>
                  <TooltipContent side="right">{linkInfo.label}</TooltipContent>
                </Tooltip>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

// Single white svg recolored via the theme token (light: #1C1A20, dark: white)
// using a CSS mask, so we no longer need a separate colored png. The mask is
// kept at full wordmark width and left-aligned; collapsing the sidebar shrinks
// the box width so only the left "Vy" of the wordmark stays visible.
function SidebarLogo() {
  return (
    <span
      className="tw:block tw:bg-sidebar-foreground tw:overflow-hidden tw:transition-all tw:duration-200 tw:h-[44px] tw:w-[97px] tw:[mask-size:auto_44px] tw:[-webkit-mask-size:auto_44px] tw:group-data-[collapsible=icon]:size-6 tw:group-data-[collapsible=icon]:[mask-size:auto_22px] tw:group-data-[collapsible=icon]:[-webkit-mask-size:auto_22px]"
      style={{
        maskImage: 'url(/logo_white.svg)',
        WebkitMaskImage: 'url(/logo_white.svg)',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'left center',
        WebkitMaskPosition: 'left center',
      }}
    />
  );
}

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  viewer: Viewer;
  onOpenDevTools?: () => void;
}

export function AppSidebar({
  viewer,
  onOpenDevTools,
  ...props
}: AppSidebarProps) {
  const { contextSidebar } = useSidebarContext();
  const navItems = getAvailableNavItems(viewer);
  const { isInteractive, body } = useSidebarLayout(viewer);
  const isMobile = useIsMobile();

  // 'empty' = no expandable nav body: public pages, plus authenticated viewers
  // with neither the role nav nor a context sidebar (e.g. store terminal /
  // unrecognized roles). The SidebarProvider is forced collapsed (see
  // SidebarShell) so it sits as a static icon rail — just the logo and the
  // footer user menu (which keeps language/theme/logout reachable). No nav body
  // and no collapse rail to expand it.
  const isMinimal = body === 'empty';
  const showMainNav = body === 'main';
  const showContextSidebar = body === 'context';

  // On mobile, collapsible="icon" renders an offcanvas drawer instead of the
  // rail, and public pages have no trigger to open it. There the shell renders a
  // top header (see AppShellWrapper) that carries the user menu instead, so the
  // sidebar itself renders nothing. Authenticated minimal viewers keep their
  // header trigger, so their drawer (and its user menu) stays reachable.
  if (!isInteractive && isMobile) {
    return null;
  }

  return (
    <Sidebar
      collapsible="icon"
      {...props}
      className={cn('tw:bg-sidebar', props.className)}
    >
      <SidebarHeader className="tw:p-0 tw:px-4 tw:pt-6 tw:h-sidebar-header-height tw:transition-[padding] tw:duration-200 tw:group-data-[collapsible=icon]:px-3 tw:group-data-[collapsible=icon]:pt-[calc((var(--spacing-sidebar-header-height)-24px)/2)]">
        {isMinimal ? (
          <span className="tw:flex tw:self-start">
            <SidebarLogo />
          </span>
        ) : (
          <Link to={Routes.home()} className="tw:flex tw:self-start">
            <SidebarLogo />
          </Link>
        )}
      </SidebarHeader>
      <SidebarSeparator className="tw:mx-0" />
      <SidebarContent className="tw:no-scrollbar tw:[&_[data-slot='sidebar-group']:last-of-type]:pb-20">
        <AnimatePresence mode="wait" initial={false}>
          {showMainNav && (
            <motion.div
              key="main-sidebar"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.18, ease: 'easeInOut' }}
            >
              <SidebarGroup>
                <SidebarGroupContent>
                  <SidebarMenu className="tw:gap-1">
                    {navItems.map((item) => (
                      <NavGroup key={item.label} item={item} />
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </motion.div>
          )}

          {showContextSidebar && contextSidebar && (
            <motion.div
              key="context-sidebar"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.18, ease: 'easeInOut' }}
            >
              <ContextSidebar config={contextSidebar} />
            </motion.div>
          )}
        </AnimatePresence>
      </SidebarContent>
      <SidebarSeparator className="tw:mx-0" />
      <SidebarFooter>
        <SidebarUserMenu viewer={viewer} onOpenDevTools={onOpenDevTools} />
      </SidebarFooter>
      {!isMinimal && <SidebarRail />}
    </Sidebar>
  );
}
